// Качество демонстрации экрана: разрешение и частота кадров, выбранные перед стартом.
// Потолок зависит от «Плюса» и уровня сервера — см. shareCaps ниже.
//
// Лесенка кончается на 4K. Раньше верхним вариантом было «как на экране» — без своего
// числа и со своим битрейтом ниже, чем у 1440p: на большом мониторе кодировщик жевал
// вчетверо больше пикселей и вгонял их в меньший поток, так что «повышение» выглядело
// хуже предыдущей ступени. Теперь у верха есть число и битрейт под него.

/*
 * Умеет ли этот браузер захватывать экран. В мобильных браузерах getDisplayMedia нет, а где
 * есть — он бесполезен: человек с телефона тапал «показать экран», получал захват без звука
 * и подсказку про галочку в окне выбора, которого на телефоне не существует.
 */
export const canShareScreen = typeof navigator !== 'undefined' && typeof navigator.mediaDevices?.getDisplayMedia === 'function'

/*
 * Звук показа экрана из браузера. Голоса собеседников играет сама вкладка Кличата, и захват звука
 * системы слышит их вместе с игрой или видео. Ограничение restrictOwnAudio (пока только Chromium)
 * просит браузер не брать в захват звук вкладки, которая захватывает, — тогда голоса не попадают
 * в трансляцию вовсе, а не вычитаются фильтром через несколько секунд. Обработка речи (эхо, шум,
 * громкость) звуку трансляции вредна — это музыка и игры, поэтому выключена явно: браузер,
 * получив объект вместо true, мог бы включить свои значения по умолчанию.
 */
export type ShareAudio =
  | boolean
  | { restrictOwnAudio: true; echoCancellation: false; noiseSuppression: false; autoGainControl: false }

export function shareAudio(o: { muted: boolean; stripVoices: boolean; supported: boolean; enabled: boolean }): ShareAudio {
  if (o.muted) return false
  // без «убирать голоса» человек сам решил слать звук как есть — как и раньше, просто true
  if (!o.stripVoices || !o.supported || !o.enabled) return true
  return { restrictOwnAudio: true, echoCancellation: false, noiseSuppression: false, autoGainControl: false }
}

/** Понимает ли браузер restrictOwnAudio: Chromium отвечает true, остальные пока молчат. */
export const restrictOwnAudioSupported =
  typeof navigator !== 'undefined' &&
  Boolean((navigator.mediaDevices?.getSupportedConstraints?.() as Record<string, boolean | undefined> | undefined)?.restrictOwnAudio)

export type ShareResolution = '720' | '1080' | '1440' | '2160'
export type ShareFps = 15 | 30 | 60

export interface ScreenShareQuality {
  resolution: ShareResolution
  fps: ShareFps
  /**
   * Не передавать звук компьютера. По умолчанию звук идёт: так ждут все, кто показывает игру
   * или видео. Голоса из канала вычитаются отдельно, чтобы собеседники не слышали самих себя.
   * Поле новое (раньше было audio с обратным смыслом) — старые настройки просто дают звук.
   */
  muteAudio: boolean
}

export const DEFAULT_SHARE_QUALITY: ScreenShareQuality = { resolution: '1080', fps: 30, muteAudio: false }

export const SHARE_RESOLUTIONS: { value: ShareResolution; label: string; hint: string }[] = [
  { value: '720', label: '720p', hint: 'экономно, для слабого канала' },
  { value: '1080', label: '1080p', hint: 'стандарт' },
  { value: '1440', label: '1440p', hint: 'чёткий текст, нужен хороший аплоад' },
  { value: '2160', label: '4K', hint: 'очень требовательно к каналу' },
]

export const SHARE_FPS: { value: ShareFps; label: string; hint: string }[] = [
  { value: 15, label: '15 к/с', hint: 'презентации и текст' },
  { value: 30, label: '30 к/с', hint: 'стандарт' },
  { value: 60, label: '60 к/с', hint: 'игры и видео' },
]

const SIZES: Record<ShareResolution, { width: number; height: number }> = {
  '720': { width: 1280, height: 720 },
  '1080': { width: 1920, height: 1080 },
  '1440': { width: 2560, height: 1440 },
  '2160': { width: 3840, height: 2160 },
}

// Потолок битрейта (бит/с) по разрешению и частоте кадров; при слабом канале LiveKit снижает сам.
const BITRATE: Record<ShareResolution, Record<ShareFps, number>> = {
  '720': { 15: 1_500_000, 30: 2_500_000, 60: 4_000_000 },
  '1080': { 15: 2_500_000, 30: 6_000_000, 60: 9_000_000 },
  '1440': { 15: 4_000_000, 30: 9_000_000, 60: 14_000_000 },
  // 22 Мбит/с — много, и это сознательно: 4K даёт третий уровень сервера, а его набирают
  // четырнадцатью бустами. Отдать вчетверо больше пикселей в битрейт 1440p значило бы
  // сделать верхнюю ступень хуже предыдущей.
  '2160': { 15: 6_000_000, 30: 14_000_000, 60: 22_000_000 },
}

/**
 * Параметры захвата getDisplayMedia для LiveKit.
 *
 * LiveKit кладёт это в ideal, а ideal — пожелание: браузер вправе отдать кадры родного
 * размера с монитора крупнее выбранного. Поэтому после старта размер ещё раз прижимается
 * жёстко — см. startScreen в store/voice.ts.
 */
export function shareCapture(q: ScreenShareQuality): { width: number; height: number; frameRate: number } {
  return { ...SIZES[q.resolution], frameRate: q.fps }
}

/** Потолок битрейта для публикации. */
export function shareBitrate(q: ScreenShareQuality): number {
  return BITRATE[q.resolution][q.fps]
}

export function shareLabel(q: ScreenShareQuality): string {
  const res = SHARE_RESOLUTIONS.find((r) => r.value === q.resolution)?.label ?? q.resolution
  return `${res} · ${q.fps} к/с · ${q.muteAudio ? 'без звука' : 'со звуком'}`
}

export function isShareResolution(v: unknown): v is ShareResolution {
  return v === '720' || v === '1080' || v === '1440' || v === '2160'
}

export function isShareFps(v: unknown): v is ShareFps {
  return v === 15 || v === 30 || v === 60
}

// --- потолок качества ------------------------------------------------------
//
// Тот же расчёт на бэкенде (internal/billing, StreamLimit): берём лучшее из личного «Плюса»
// и уровня сервера. Считает клиент — разрешение и кадры задаёт захват на его стороне,
// и удержать их на сервере можно было бы только перекодированием.
//
// Ограничение тут не ради денег: 1440p60 — это до 14 Мбит/с, и SFU отдаёт копию каждому
// зрителю. Впятером с одной трансляции набегает 70 Мбит/с исходящих.

export interface ShareCaps {
  height: number
  fps: ShareFps
}

const FREE_CAPS: ShareCaps = { height: 1080, fps: 30 }
const PLUS_CAPS: ShareCaps = { height: 1080, fps: 60 }
const GUILD_CAPS: ShareCaps[] = [
  { height: 1080, fps: 30 },
  { height: 1080, fps: 60 },
  { height: 1440, fps: 60 },
  { height: 2160, fps: 60 },
]

/** Высота картинки для варианта разрешения. */
export const SHARE_HEIGHT: Record<ShareResolution, number> = { '720': 720, '1080': 1080, '1440': 1440, '2160': 2160 }

/** Подпись высоты: 2160 все называют 4K, остальные — числом. Одна на окно выбора и таблицу уровней. */
export function heightLabel(px: number): string {
  return px >= 2160 ? '4K' : `${px}p`
}

/** Что доступно этому человеку на этом сервере (вне сервера — уровень 0). */
export function shareCaps(plus: boolean, guildTier = 0): ShareCaps {
  const guild = GUILD_CAPS[Math.min(Math.max(guildTier, 0), 3)] ?? FREE_CAPS
  const user = plus ? PLUS_CAPS : FREE_CAPS
  return { height: Math.max(user.height, guild.height), fps: Math.max(user.fps, guild.fps) as ShareFps }
}

/**
 * Чем открывается такое качество: строка для подсказки под заблокированным вариантом,
 * null — доступно всем. Уровень ищем первый подходящий: выше по списку дают не меньше.
 */
export function shareUnlock(height: number, fps: ShareFps): string | null {
  const fits = (c: ShareCaps) => c.height >= height && c.fps >= fps
  if (fits(FREE_CAPS)) return null
  const tier = GUILD_CAPS.findIndex(fits)
  const byPlus = fits(PLUS_CAPS)
  if (byPlus && tier > 0) return `Кличат Плюс или ${tier}-й уровень сервера`
  if (byPlus) return 'Кличат Плюс'
  if (tier > 0) return `${tier}-й уровень сервера`
  return null
}

/** Опускает сохранённый выбор до доступного: настройки переживают и конец «Плюса», и снятие бустов. */
export function clampShareQuality(q: ScreenShareQuality, caps: ShareCaps): ScreenShareQuality {
  const resolution = SHARE_HEIGHT[q.resolution] > caps.height
    ? ([...SHARE_RESOLUTIONS].reverse().find((r) => SHARE_HEIGHT[r.value] <= caps.height)?.value ?? '720')
    : q.resolution
  const fps = q.fps > caps.fps ? caps.fps : q.fps
  return resolution === q.resolution && fps === q.fps ? q : { ...q, resolution, fps }
}
